import React, { useEffect } from 'react';
import { X, LayoutGrid, CalendarDays, Bell, Archive, HelpCircle, User, LogOut } from 'lucide-react';
import logo from '../imgs/Logo_Fcn.png';

const HamburgerMenu = ({
  show,
  onClose,
  activeTab,
  setActiveTab,
  onShowNotifications,
  onShowFileLibrary,
  onShowHelpSupport,
  onShowProfile,
  onShowLogout,
  unreadNotifications,
  user
}) => {
  // Cerrar menú con tecla Escape y bloquear el scroll del fondo mientras está abierto
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && show) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [show, onClose]);

  if (!show) return null;

  const run = (fn) => () => {
    fn();
    onClose();
  };

  const menuItems = [
    { id: 'desktop', label: 'Aplicaciones', icon: LayoutGrid, action: run(() => setActiveTab('desktop')) },
    { id: 'calendar', label: 'Calendario', icon: CalendarDays, action: run(() => setActiveTab('calendar')) },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: unreadNotifications, action: run(onShowNotifications) },
    { id: 'files', label: 'Archivos', icon: Archive, action: run(onShowFileLibrary) },
    { id: 'help', label: 'Ayuda y soporte', icon: HelpCircle, action: run(onShowHelpSupport) },
    { id: 'profile', label: 'Mi perfil', icon: User, action: run(onShowProfile) },
  ];

  const displayName = user?.empleado?.NombreEmpleado || user?.usuario || 'Usuario';
  const initial = displayName.trim().charAt(0).toUpperCase();

  return (
    <div className="fixed inset-0 z-50 lg:hidden text-left">
      <div
        className="absolute inset-0 bg-cafe-800 bg-opacity-60"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-white shadow-[0_40px_90px_-25px_rgba(59,39,25,0.4)] animate-slide-in-left">
        <div className="flex items-center justify-between px-5 py-5 border-b border-cafe-100">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Logo Fondo Cafetero" className="h-9" />
            <span className="text-[11px] uppercase tracking-[0.25em] text-cafe-400 font-semibold">Portal FCN</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar menú"
            className="rounded-lg p-2 text-neutral-300 hover:bg-cafe-100/50 hover:text-cafe-700 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-3 px-5 py-4 border-b border-cafe-100">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-cafe-200/60 text-sm font-semibold text-cafe-700">
            {initial}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-cafe-800">{displayName}</p>
            <p className="truncate text-xs text-neutral-300">
              @{user?.usuario} · {user?.empleado?.Puesto || 'Empleado'}
            </p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" aria-label="Navegación principal">
          {menuItems.map(({ id, label, icon: Icon, badge, action }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={action}
                aria-current={isActive ? 'page' : undefined}
                className={`relative flex w-full items-center justify-between rounded-lg px-3 py-3 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-cafe-700 text-white shadow-[0_6px_16px_-8px_rgba(62,39,35,0.7)]'
                    : 'text-neutral-300 hover:bg-cafe-700/10 hover:text-cafe-700'
                }`}
              >
                {isActive && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-sol-400" />}
                <span className="flex items-center gap-3">
                  <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                  {label}
                </span>
                {badge > 0 && (
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-secondary-700 px-1.5 text-[11px] font-semibold text-white">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-cafe-100 p-3">
          <button
            onClick={run(onShowLogout || (() => {}))}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-neutral-300 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
          >
            <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
            Cerrar sesión
          </button>
          <p className="mt-3 px-3 text-xs text-neutral-300">© {new Date().getFullYear()} Fondo Cafetero Nacional</p>
        </div>
      </div>
    </div>
  );
};

export default HamburgerMenu;
